import { ROLE_CENSORED_TOKEN, USER_AVATAR_TYPE_OLD } from "@/utils/variables";
import { Profile } from "@/interfaces/User/Profile";
import { useRoleStore } from "@/store/role.ts";
import { getRandomCensoredImage } from "@/utils/censorHelper.ts";
import browserHelper from "@/utils/browserHelper.ts";
import defaultAvatar from "@/assets/images/user_profile/default_avatar.svg";

export const getMediaUrl = (dir: string, name: string): string => {
  return `${import.meta.env.VITE_MEDIA_CONTENT_HOST}/${dir}/${name}`;
};

const getThumbnailUrl = (dir: string, name: string, width: number, height: number, fill: boolean = true, sharpen: number = 1.0) => {
  if (useRoleStore().hasRole(ROLE_CENSORED_TOKEN)) {
    return getRandomCensoredImage();
  }

  const rt = fill ? "fill" : "fit";
  const format = browserHelper.isWebpSupported ? "webp" : "jpg";
  const source = name ? `${dir}/${encodeURIComponent(name)}` : "content/images_mysql/img-placeholder.jpg";

  return `${import.meta.env.VITE_THUMB_HOST}/insecure/sharpen:${sharpen}/rs:${rt}:${width}:${height}/plain/${import.meta.env.VITE_STORAGE_HOST}/${source}@${format}`;
};

export const getProfileAvatar = (profile: Profile) => {
  if (profile.avatar === null) {
    return defaultAvatar;
  }

  return profile.avatar === USER_AVATAR_TYPE_OLD ? getMediaUrl("upload/avatars", `${profile.id}.svg`) : getNewAvatar(`${profile.avatar}.svg`);
};

export const getNewAvatar = (name: string) => {
  return getMediaUrl("upload/avatars/new", name);
};

export const getVideoTimelinePreview = (name: string) => {
  if (useRoleStore().hasRole(ROLE_CENSORED_TOKEN)) {
    return getRandomCensoredImage();
  }

  return getMediaUrl("content/images_mysql/video_timeline_previews", name);
};

export const getWhoWeAreVideoUrl = (lang: string) => {
  return getMediaUrl(
    "content/videos/about_us/recoded",
    `,160/team_${lang}.mp4,240/team_${lang}.mp4,360/team_${lang}.mp4,480/team_${lang}.mp4,720/team_${lang}.mp4,1080/team_${lang}.mp4,.urlset/master.m3u8`,
  );
};

export const getBehindTheScenesUrl = (): string => {
  return getMediaUrl(
    "content/videos/about_us/recoded",
    `,160/bts.mp4,240/bts.mp4,360/bts.mp4,480/bts.mp4,720/bts.mp4,1080/bts.mp4,.urlset/master.m3u8`,
  );
};

export const getBehindTheScenesUrlV2 = (): string => {
  return getMediaUrl(
    "content/videos/about_us/recoded",
    `,160/bts_v2.mp4,240/bts_v2.mp4,360/bts_v2.mp4,480/bts_v2.mp4,720/bts_v2.mp4,1080/bts_v2.mp4,.urlset/master.m3u8`,
  );
};

export const getVideoPoster = (name: string) => {
  return getThumbnailUrl("content/images_mysql/images_videothumbnails/backup", name, 900, 500);
};

export const getVideoPosterMini = (name: string) => {
  return getThumbnailUrl("content/images_mysql/images_videothumbnails/backup", name, 370, 206);
};

export const getPublicVideoPoster = (name: string) => {
  return getThumbnailUrl("content/images_mysql/images_videothumbnails_public/backup", name, 900, 500);
};

export const getVideoThumbnail = (name: string) => {
  return getThumbnailUrl("content/images_mysql/images_videothumbnails/backup", name, 400, 490);
};

export const getFreeareaVideoPoster = (name: string) => {
  return getThumbnailUrl("upload/CMS/freearea", name, 900, 500, true, 0);
};

export const getFreeareaVideoPosterBig = (name: string) => {
  return getThumbnailUrl("upload/CMS/freearea", name, 1280, 720, true, 0);
};

export const getGalleryImageThumb = (name: string) => {
  return getThumbnailUrl("content/images_mysql/Model_Images/backup", name, 400, 400);
};

export const getGalleryImageMediumThumb = (name: string) => {
  return getThumbnailUrl("content/images_mysql/Model_Images/backup", name, 600, 600);
};

export const getGalleryImage = (name: string) => {
  return getThumbnailUrl("content/images_mysql/Model_Images/backup", name, 1920, 1080, false, 0);
};

export const getUpdateFullThumb = (name: string) => {
  return getThumbnailUrl("content/images_mysql/Model_Recent_Image/backup", name, 800, 450, false, 0);
};

export const getUpdateMiniThumb = (name: string) => {
  return getThumbnailUrl("content/images_mysql/Model_Recent_Image/backup", name, 660, 440, false);
};

export const getUpdateBlogThumb = (name: string) => {
  return getThumbnailUrl("content/images_mysql/Model_Recent_Image/backup", name, 600, 1500, false, 0);
};

export const getPublicModelThumb = (name: string, width = 510, height = 660, adaptive = true) => {
  return getThumbnailUrl("content/images_mysql/Model_Cover_Image_Unsubscribed/backup", name, width, height, !adaptive);
};

export const getModelThumb = (name: string, width = 510, height = 660) => {
  return getThumbnailUrl("content/images_mysql/Model_Cover_Image/backup", name, width, height);
};

export const getGalleryThumb = (name: string, width = 510, height = 660) => {
  return getThumbnailUrl("content/images_mysql/Shoot_Cover", name, width, height);
};

export const getModelThumbMini = (name: string) => {
  return getThumbnailUrl("content/images_mysql/Model_Cover_Image/backup", name, 270, 360, false);
};

export const getPlaydateCameraThumb = (name: string) => {
  return getThumbnailUrl("content/images_mysql/Playdate_Image/backup", name, 340, 440);
};

export const getPublicPlaydateItemThumb = (name: string) => {
  return getThumbnailUrl("content/images_mysql/Playdate_Image_Page/backup", name, 450, 1500, false);
};

export const getCustomNewAndUpcomingThumb = (name: string) => {
  return getThumbnailUrl("upload/CMS/new_upcoming", name, 510, 660);
};

export const getBlogPostThumb = (dir: string, name: string, width: number) => {
  return getThumbnailUrl(dir, name, width, width, false, 0);
};

export const getPollHeaderImage = (name: string) => {
  return getMediaUrl("content/images_mysql/images_polls/backup", name);
};

export const getDocumentUrl = (name: string) => {
  return getMediaUrl("content/documents", name);
};

export const getBlogPostVideoMedia = (name: string) => {
  return getMediaUrl("upload/CMS/embed_videos", name);
};

export const getBlogPostVideoPoster = (name: string) => {
  return getThumbnailUrl("upload/CMS/embed_videos", name, 1280, 720, true, 0);
};
